import { Footer } from "@/components/Footer";
import { PageMeta } from "@/components/site/blocks";
import { Band, Closing, Faqs, Hero, In, List, Related, Src, Steps, Table, WhatIs, WorkflowCard } from "@/components/seo/kit";

// /slack-integration: Slack automation with ClearMakes Inbox. Rewritten 2026-09-29 for today's
// product (SEO run: gtm/products/clearmakes-inbox/seo/). Slack facts opened 2026-09-29.
// 2026-10-22: title, H1 and an IMAP question aimed at "email slack integration" and "imap slack
// integration" (Search Console, position 49-57).

const PATH = "/slack-integration";

const faqs = [
  {
    q: "Does it work on Slack's free plan?",
    a: "Yes. Inbox connects as an app, and Slack's free plan allows up to ten apps. Slack's own email address for a channel needs a paid Slack plan; Inbox doesn't use it.",
  },
  {
    q: "Does it work with any email account, such as IMAP?",
    a: "Not yet. Inbox starts workflows from Gmail and Outlook (including Microsoft 365) mailboxes, not from any IMAP server. If your mail is somewhere else, forward it to a Gmail or Outlook mailbox and start the workflow there, or use Slack's own channel email address on a paid Slack plan.",
  },
  {
    q: "Can a Slack message start a workflow?",
    a: "Yes. A workflow can start when a message arrives in Slack, for example in a channel you name, and then email someone, create a task or post somewhere else.",
  },
  {
    q: "How is this different from Slack Workflow Builder?",
    a: "Workflow Builder runs inside Slack. Inbox starts from your email as well as Slack, and reaches Gmail, Outlook, HubSpot and Notion in the same workflow. If your whole job lives inside Slack, Workflow Builder may be enough.",
  },
  {
    q: "Will Inbox read all my Slack messages?",
    a: "No. It reads only what a workflow you switched on needs, and posts only what your workflows say to post. You can disconnect Slack from the Apps page at any time.",
  },
];

const SlackIntegration = () => (
  <>
    <PageMeta
      title="Email to Slack Integration: Post the Emails That Matter"
      description="An email to Slack integration for Gmail and Outlook: post the emails that matter to the right channel with a summary, or start work from a Slack message."
      path={PATH}
      crumbs={[
        { name: "Home", path: "/" },
        { name: "Integrations", path: "/integrations" },
        { name: "Slack", path: PATH },
      ]}
    />
    <main>
      <Hero
        eyebrow="Integrations · Slack"
        title="Email to Slack integration that posts only what matters"
        lead="Post the right emails to the right Slack channel with a summary, or let a Slack message start a workflow that reaches your other apps."
        secondary={{ label: "See every integration", href: "/integrations" }}
        updated="22 October 2026"
      />

      <WhatIs title="What does ClearMakes Inbox do with Slack?">
        ClearMakes Inbox connects to Slack so your workflows can post to channels and people, and so a new Slack message can start a workflow. A
        common use is posting important Gmail or Outlook emails to a channel with a short summary. You describe each workflow in one sentence.
        It works on Slack's free plan.
      </WhatIs>

      <Band tone="pale" title="What a Slack workflow can do" answer="Slack is where a workflow tells people things, and it can also be where a workflow starts. Both work in the same account.">
        <div className="grid gap-5 lg:grid-cols-2">
          <WorkflowCard
            sentence="When a customer emails support@, post the sender, subject and a two-line summary to #support."
            steps={["When an email arrives in Gmail", "Check it is from a customer", "Summarise it", "Post to #support in Slack"]}
          />
          <WorkflowCard
            sentence="When someone posts in #leads with an email address, add them to HubSpot."
            steps={["When a message arrives in Slack", "Check it contains an email address", "Pull out the details: name, email, company", "Create the contact in HubSpot"]}
          />
        </div>
        <List
          items={[
            <>From email: <In href="/integrations/gmail-to-slack">Gmail to Slack</In> and <In href="/integrations/outlook-to-slack">Outlook to Slack</In>.</>,
            "From Slack: email a person, create a task, add a Notion page or a HubSpot contact.",
          ]}
        />
      </Band>

      <Band tone="white" title="How to connect Slack" answer="Sign in to Slack from the Apps page and pick the workspace. If only admins can add apps in your workspace, ask one first.">
        <Steps
          items={[
            { title: "Create a free ClearMakes account", body: <>Start from <In href="/products/inbox">ClearMakes Inbox</In>.</> },
            { title: "Open Apps and connect Slack", body: "Slack shows what the app may do before you agree." },
            { title: "Describe a workflow and check the steps", body: "Name the channel in the sentence, answer at most one question, then switch it on." },
          ]}
        />
      </Band>

      <Band tone="pale" title="Other ways to automate Slack" answer="Slack has its own Workflow Builder, and its pages say building workflows from a single prompt is on Business+ and Enterprise+. General tools like Zapier connect Slack to thousands of apps.">
        <Table
          caption="Ways to automate Slack"
          head={["Way", "What it costs", "Good at", "Watch out for"]}
          rows={[
            ["Slack Workflow Builder", "Included in Slack plans; Slack's pricing and feature pages disagree on whether Free has it", "Forms, approvals and steps inside Slack", "Starting from Gmail or Outlook email is not its focus"],
            ["Slack's channel email address", "Paid Slack plans", "Copying emails into a channel", "No choosing or summarising; Microsoft 365 often blocks forwarding to it"],
            ["Zapier (Slack)", "Free: 100 tasks a month. Professional: $29.99 a month for 750 tasks", "30+ Slack actions; thousands of apps", "Each successful action is a task"],
            ["ClearMakes Inbox", "Free: 3 apps, 100 runs a month. Starter: $29 a month for 5,000 runs", "Email and Slack in one workflow, described in a sentence", "Only starts from messages; newer company"],
          ]}
          note={
            <>
              Checked on 29 September 2026: <Src href="https://slack.com/intl/en-us/pricing">Slack pricing</Src> (free plan: up to ten apps),{" "}
              <Src href="https://slack.com/features/workflow-automation">Slack workflow automation</Src>,{" "}
              <Src href="https://slack.com/help/articles/206819278-Send-emails-to-Slack">Slack, Send emails to Slack</Src>, <Src href="https://zapier.com/pricing">Zapier pricing</Src>.
            </>
          }
        />
      </Band>

      <Band tone="white" title="Limits, and who this is not for" answer="If your whole job happens inside Slack, such as a form and an approval, Slack's own Workflow Builder is the simpler tool." narrow>
        <List
          items={[
            "Not a Slack chatbot for answering your team's questions; for that, look at ClearMakes Agents.",
            "AI summaries can be wrong now and then; check the first posts.",
            "Free: 3 apps, 2 new workflows a month, 100 runs a month.",
          ]}
        />
      </Band>

      <Faqs items={faqs} />

      <Related
        links={[
          { href: "/integrations/gmail-to-slack", label: "Gmail to Slack", desc: "Post the Gmail emails that matter." },
          { href: "/integrations/outlook-to-slack", label: "Outlook to Slack", desc: "The same for Outlook mailboxes." },
          { href: "/guides/forward-email-to-slack-channel", label: "Forward emails to a Slack channel", desc: "Every way to do it, free ones first." },
          { href: "/guides/parse-emails-to-slack", label: "Email parser to Slack", desc: "Post the order number or amount, not the whole email." },
          { href: "/for-remote-teams", label: "For remote teams", desc: "The right email in the right channel, across time zones." },
          { href: "/teams-integration", label: "Microsoft Teams automation", desc: "If your team is on Teams." },
          { href: "/pricing", label: "Pricing", desc: "Free, Starter, Growth and Enterprise." },
        ]}
      />

      <Closing title="Put the right messages in the right channel." line="Connect Slack and describe your first workflow in one sentence." />
    </main>
    <Footer />
  </>
);

export default SlackIntegration;
